import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Card, Modal, Select, Space, Spin, Tooltip, Typography } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { downloadBlob } from '@/lib/download'
import { SortControls } from '../../practice-agent/workspace/components/SortControls'
import type { HydratedWidgetResult } from '../widgetData'
import { useDashboardDataSource } from '../dataSource'
import { hasWidgetHydrationPayload } from '../builderCore'
import { DonutViz } from '../DonutViz'
import { describeDateRangeSubtitle } from '../datePresets'
import { resolveGaugeBadge, resolveGaugeValues } from '../gauge'
import { GaugeBadge, GaugeViz } from '../GaugeViz'
import { mapWidgetToAgentBuilderData } from '../presentation'
import {
  buildEffectiveDaxGenerationRequest,
  buildLegacySelectedFilters,
  defaultMeasureParameterSelections,
  getCatalogDatePresetOptions,
} from '../runtime'
import { buildWidgetSortOptions, resolveDefaultDateSortKey, resolveWidgetSortKey } from '../sortUtils'
import { BarViz, DataBarTableViz, GroupedBarViz, KpiViz, LineViz, PivotTableViz, TableViz, type ExpandedTableResult } from '../Viz'
import { buildExpansionRowKey, resolvePivotColumnField, trendGranularityOptions, type WidgetPresentationState } from '../vizModel'
import { VIZ_SWITCHER, getVizSwitcherOptions } from '../vizSwitcher'
import {
  createDisabledDrillOrder,
  findDrilldownColumn,
  getFieldSelectionDimensions,
  resolveDrillOrder,
  type DrilldownHierarchyNode,
  type DrilldownPathFilter,
} from '../drilldownHierarchy'
import { labelsMatch, stripTableQualifier } from '../labelMatching'
import { formatFieldDisplayName } from '../vizUtils'
import { resolveDefaultWidgetPageSize, type WidgetSessionState } from '../viewer/engine/state'
import type {
  DashboardCatalogPlatform,
  DashboardDefinition,
  DashboardFilter,
  DashboardRuntimeState,
  DashboardTrendGranularity,
  DashboardWidget,
  DashboardWidgetType,
  WidgetHydrationState,
} from '../types'

// Mock-up `.select`: 30px high, 6px radius, #d0d6de border, 13px text.
const CARD_SELECT_CLASS = '[&_.ant-select-selector]:!h-[30px] [&_.ant-select-selector]:!rounded-md [&_.ant-select-selector]:!border-[#d0d6de] [&_.ant-select-selector]:!px-2.5 [&_.ant-select-selection-item]:!text-[13px] [&_.ant-select-selection-item]:!leading-[28px] [&_.ant-select-selection-placeholder]:!text-[13px] [&_.ant-select-selection-placeholder]:!leading-[28px]'

type LegacyAgentBuilderWidgetPanelProps = {
  clientId: string
  dashboardId?: string | null
  dashboard: DashboardDefinition | null
  widget: DashboardWidget
  widgetState: WidgetHydrationState
  catalog: DashboardCatalogPlatform[] | null
  dashboardFilters: DashboardFilter[]
  selectedFilters: Record<string, string | string[]>
  runtimeState: DashboardRuntimeState
  onWidgetStateChange: (widgetId: string, state: WidgetHydrationState) => void
  onWidgetRuntimeStateChange?: (widgetId: string, state: { groupByDimension?: string | null; groupByDimensions?: string[] | null }) => void
  mode?: 'builder' | 'viewer'
  printMode?: boolean
}

type EngineAgentBuilderWidgetPanelProps = {
  clientId: string
  dashboardId?: string | null
  dashboard: DashboardDefinition | null
  widget: DashboardWidget
  session: WidgetSessionState
  catalog: DashboardCatalogPlatform[] | null
  dashboardFilters: DashboardFilter[]
  selectedFilters: Record<string, string | string[]>
  runtimeState: DashboardRuntimeState
  onSort: (widgetId: string, column: string | null, direction: 'asc' | 'desc') => void
  onPage: (widgetId: string, page: number) => void
  onDimensionChange: (widgetId: string, groupByDimensions: string[] | null) => void
  onMeasureParameterChange?: (widgetId: string, selections: Record<string, string> | null) => void
  onPickerStateChange: (widgetId: string, pickerState: { field: string; value: string } | null) => void
  onDrilldown: (widgetId: string, drilldown: { field: string; value: string; dimension: string }) => void
  onClearDrilldown: (widgetId: string) => void
  /** Reports card-local presentation state (trend granularity, expanded drilldown rows) so the viewer can print it. */
  onPresentationStateChange?: (widgetId: string, state: WidgetPresentationState) => void
  mode?: 'builder' | 'viewer'
  printMode?: boolean
  /** Across the bottom of the card in the viewer, e.g. a visual's XPM source in Claude. */
  footer?: ReactNode
  /** Claude's lines come already bucketed by the API, so Daily would only mislabel them. */
  hideTrendGranularity?: boolean
}

type AgentBuilderWidgetPanelProps = LegacyAgentBuilderWidgetPanelProps | EngineAgentBuilderWidgetPanelProps

function toWidgetState(result: HydratedWidgetResult): WidgetHydrationState {
  return {
    status: 'live',
    stage: 'execute_dax_query',
    rows: result.rows,
    columns: result.columns,
    chartData: result.chartData,
    comparisonChartData: result.comparisonChartData,
    kpiCards: result.kpiCards,
    daxQuery: result.daxQuery,
    hasPaginationMetadata: result.hasPaginationMetadata,
    totalRows: result.totalRows,
    page: result.page,
    pageSize: result.pageSize,
    sortColumn: result.sortColumn,
    sortDirection: result.sortDirection,
    groupByDimension: result.groupByDimension,
    axisMinValue: result.axisMinValue,
    axisMaxValue: result.axisMaxValue,
    axisMinByMeasure: result.axisMinByMeasure,
    axisMaxByMeasure: result.axisMaxByMeasure,
    columnFormats: result.columnFormats,
    lastUpdatedAt: new Date().toISOString(),
  }
}

function getWidgetVizType(type: DashboardWidgetType) {
  return type === 'groupedBar'
    ? 'GroupedBar'
    : type === 'line'
      ? 'Line'
      : type === 'kpi'
        ? 'KPI'
        : type === 'donut'
          ? 'Donut'
          : type === 'table' || type === 'dataBarTable' || type === 'pivotTable'
            ? 'Table'
            : 'Bar'
}

function isTableLikeViz(type: DashboardWidgetType) {
  return type === 'table' || type === 'dataBarTable' || type === 'pivotTable'
}

function isExportableViz(type: DashboardWidgetType) {
  return type === 'bar'
    || type === 'groupedBar'
    || type === 'line'
    || type === 'donut'
    || type === 'table'
    || type === 'dataBarTable'
    || type === 'pivotTable'
}

function hasPanelSortControls(type: DashboardWidgetType) {
  return type !== 'kpi'
    && type !== 'gauge'
    && type !== 'bar'
    && type !== 'groupedBar'
    && type !== 'line'
    && !isTableLikeViz(type)
}

function getDrilldownDimensions(hierarchy: DrilldownHierarchyNode[] | null | undefined) {
  return Array.from(new Set((hierarchy ?? []).map((node) => node.field).filter((field): field is string => Boolean(field))))
}

function normalizeDimensionSelection(
  widget: DashboardWidget,
  rawDimensions: Array<string | null | undefined>,
) {
  const optionValues = widget.dimensions.map((dimension) => ({
    value: dimension.value || dimension.name,
    aliases: [dimension.value, dimension.name].filter(Boolean),
  }))

  return rawDimensions.flatMap((rawDimension) => {
    if (!rawDimension) return []
    const match = optionValues.find((option) => option.aliases.includes(rawDimension))
    return match ? [match.value] : [rawDimension]
  })
}

function limitDimensionsForViz(type: DashboardWidgetType, dimensions: string[]) {
  return type === 'line' ? dimensions.slice(0, 1) : dimensions
}

function hasRenderableData(session: WidgetSessionState) {
  return session.data.rows.length > 0
    || session.data.columns.length > 0
    || session.data.chartData.length > 0
    || session.data.kpiCards.length > 0
}

function buildPresentationState(session: WidgetSessionState): WidgetHydrationState {
  return {
    status: session.status === 'loading' && !hasRenderableData(session)
      ? 'loading'
      : session.status === 'error' && !hasRenderableData(session)
        ? 'error'
        : 'live',
    stage: 'execute_dax_query',
    rows: session.data.rows,
    columns: session.data.columns,
    chartData: session.data.chartData,
    comparisonChartData: session.data.comparisonChartData,
    kpiCards: session.data.kpiCards,
    daxQuery: session.data.daxQuery,
    error: session.error,
    hasPaginationMetadata: session.data.hasPaginationMetadata,
    totalRows: session.data.totalRows,
    page: session.data.page,
    pageSize: session.data.pageSize,
    sortColumn: session.data.sortColumn,
    sortDirection: session.data.sortDirection,
    groupByDimension: session.query.groupByDimension ?? session.data.groupByDimension,
    axisMinValue: session.data.axisMinValue,
    axisMaxValue: session.data.axisMaxValue,
    axisMinByMeasure: session.data.axisMinByMeasure,
    axisMaxByMeasure: session.data.axisMaxByMeasure,
    columnFormats: session.data.columnFormats,
    lastUpdatedAt: session.data.lastUpdatedAt,
  }
}

function isLegacyProps(props: AgentBuilderWidgetPanelProps): props is LegacyAgentBuilderWidgetPanelProps {
  return 'widgetState' in props
}

export function AgentBuilderWidgetPanel(props: AgentBuilderWidgetPanelProps) {
  const { addToast } = useToast()
  const dataSource = useDashboardDataSource()
  const printMode = props.printMode ?? false
  const allowFieldSelection = !printMode
  const allowVizSwitch = !printMode && props.mode !== 'viewer'
  const [vizOverride, setVizOverride] = useState<DashboardWidgetType | null>(null)
  const [localPage, setLocalPage] = useState(1)
  const [trendGranularity, setTrendGranularity] = useState<DashboardTrendGranularity>(props.widget.trendGranularity ?? 'daily')
  const [isFetching, setIsFetching] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [pendingSortState, setPendingSortState] = useState<{ column: string | null; direction: 'asc' | 'desc' } | null>(null)
  const [expandedRows, setExpandedRows] = useState<Record<string, ExpandedTableResult>>({})
  const [loadingRowKeys, setLoadingRowKeys] = useState<Record<string, boolean>>({})
  const [pendingExpansion, setPendingExpansion] = useState<{
    row: Record<string, unknown>
    columns: string[]
    parentKey?: string
    rowKey: string
    currentField: string
    dimension: string
    options: string[]
  } | null>(null)
  const sortDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dimensionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const measureParameterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fetchRequestRef = useRef(0)

  const engineMeasureParameterSelections = isLegacyProps(props)
    ? null
    : props.session.query.measureParameterSelections ?? null
  const [localMeasureParameterSelections, setLocalMeasureParameterSelections] = useState<Record<string, string>>(
    () => defaultMeasureParameterSelections(props.widget),
  )
  const previousWidgetIdRef = useRef(props.widget.id)
  useEffect(() => {
    if (previousWidgetIdRef.current !== props.widget.id) {
      previousWidgetIdRef.current = props.widget.id
      setLocalMeasureParameterSelections(defaultMeasureParameterSelections(props.widget))
    }
  }, [props.widget])
  const measureParameterSelections: Record<string, string> = engineMeasureParameterSelections
    ?? localMeasureParameterSelections

  function handleMeasureParameterChange(parameterId: string, optionId: string) {
    const next = { ...measureParameterSelections, [parameterId]: optionId }
    if (!isLegacyProps(props) && props.onMeasureParameterChange) {
      props.onMeasureParameterChange(props.widget.id, next)
      return
    }
    setLocalMeasureParameterSelections(next)
    if (measureParameterDebounceRef.current) clearTimeout(measureParameterDebounceRef.current)
    measureParameterDebounceRef.current = setTimeout(() => {
      void fetchWidgetPage({ page: 1, measureParameterSelections: next })
    }, 400)
  }

  const legacyMode = isLegacyProps(props)
  const legacySelectedFilters = legacyMode ? props.selectedFilters : null
  const filterSignature = `${JSON.stringify(props.runtimeState.slicerValues)}|${JSON.stringify(props.runtimeState.dateRange)}|${JSON.stringify(props.selectedFilters)}`

  useEffect(() => () => {
    if (sortDebounceRef.current) clearTimeout(sortDebounceRef.current)
    if (dimensionDebounceRef.current) clearTimeout(dimensionDebounceRef.current)
    if (measureParameterDebounceRef.current) clearTimeout(measureParameterDebounceRef.current)
  }, [])

  useEffect(() => {
    if (!legacyMode) return
    setLocalPage(1)
    setExpandedRows({})
    setLoadingRowKeys({})
  }, [legacyMode, legacySelectedFilters, props.widget.id])

  useEffect(() => {
    setExpandedRows({})
    setLoadingRowKeys({})
  }, [filterSignature, props.widget.id])

  useEffect(() => {
    setTrendGranularity(props.widget.trendGranularity ?? 'daily')
  }, [props.widget.id, props.widget.trendGranularity])

  const onPresentationStateChange = isLegacyProps(props) ? undefined : props.onPresentationStateChange
  useEffect(() => {
    onPresentationStateChange?.(props.widget.id, { trendGranularity, expandedRows })
  }, [onPresentationStateChange, props.widget.id, trendGranularity, expandedRows])

  const effectiveViz: DashboardWidgetType = vizOverride ?? props.widget.type
  const sourceState = legacyMode
    ? props.widgetState
    : buildPresentationState(props.session)
  const data = mapWidgetToAgentBuilderData(props.widget, sourceState)
  const usesSingleDimensionSelection = effectiveViz === 'line' || props.widget.allowMultiFieldSelection !== true
  // Mock-up card chrome (viewer, screen only): 10px radius, light border, white 50px header.
  const isViewerChrome = props.mode === 'viewer' && !printMode
  const viewerCardStyles = isViewerChrome
    ? {
        root: {
          background: '#fff',
          borderRadius: 10,
          border: '1px solid #e6eaef',
          boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
          overflow: 'hidden',
        } as const,
        header: {
          background: '#fff',
          borderBottom: '1px solid #e6eaef',
          minHeight: 50,
          padding: '0 24px',
        } as const,
        title: { padding: 0 } as const,
        body: { padding: '18px 24px 24px' } as const,
      }
    : undefined
  const widgetTitle = isViewerChrome ? (
    <span className="flex items-center gap-2.5 text-[15px] font-semibold leading-tight text-[#1d2433]">
      <span className="h-[18px] w-[3px] rounded-full bg-[var(--feature-primary)]" aria-hidden />
      <span>{props.widget.title}</span>
    </span>
  ) : props.widget.title
  const footer = !isLegacyProps(props) && !printMode ? props.footer : null
  const printWidgetClass = isTableLikeViz(effectiveViz)
    ? 'dashboard-print-widget dashboard-print-table-widget'
    : 'dashboard-print-widget dashboard-print-visual-widget'
  const allowSortControls = !printMode && hasPanelSortControls(effectiveViz)
  const usesBackendPagination = sourceState.hasPaginationMetadata === true
    && sourceState.totalRows != null
    && sourceState.page != null
    && sourceState.pageSize != null
  const displayPage = usesBackendPagination
    ? (data.page ?? 1)
    : legacyMode
      ? localPage
      : props.session.query.page
  const availableTypes = props.widget.availableTypes ?? VIZ_SWITCHER.filter((v) => v.key !== 'insight').map((v) => v.key)
  const sortOptions = useMemo(
    () => buildWidgetSortOptions(props.widget, data.columns).map((option) => ({
      value: option.sortKey,
      label: formatFieldDisplayName(option.label),
    })),
    [props.widget, data.columns],
  )
  const showSortControls = allowSortControls && sortOptions.length > 1
  const hideTrendGranularity = !isLegacyProps(props) && props.hideTrendGranularity === true
  const showTrendGranularityControl = !printMode && effectiveViz === 'line' && !hideTrendGranularity
  const defaultDateSortColumn = resolveDefaultDateSortKey(props.widget)
  const defaultSortColumn = defaultDateSortColumn ?? sortOptions[0]?.value ?? resolveWidgetSortKey(props.widget, data.columns[0] ?? null)
  const defaultSortDirection = defaultDateSortColumn ? 'asc' : 'desc'
  const displaySortColumn = resolveWidgetSortKey(
    props.widget,
    legacyMode
      ? (pendingSortState?.column ?? sourceState.sortColumn ?? data.sortColumn ?? defaultSortColumn)
      : (props.session.ui.pendingSort?.column ?? props.session.query.sortColumn ?? sourceState.sortColumn ?? defaultSortColumn),
  )
  const displayTableSortColumn = useMemo(
    () => data.columns.find((column) => resolveWidgetSortKey(props.widget, column) === displaySortColumn) ?? displaySortColumn,
    [data.columns, displaySortColumn, props.widget],
  )
  const displaySortDirection = legacyMode
    ? (pendingSortState?.direction ?? sourceState.sortDirection ?? defaultSortDirection)
    : (props.session.ui.pendingSort?.direction ?? props.session.query.sortDirection ?? sourceState.sortDirection ?? defaultSortDirection)
  const drilldownHierarchy = dataSource.useDrilldownHierarchy(props.clientId, props.dashboard?.platformTypeId ?? 0)
  const drilldownDimensions = useMemo(() => getDrilldownDimensions(drilldownHierarchy), [drilldownHierarchy])
  const clientCatalogDimensions = useMemo(
    () => props.catalog?.find((platform) => platform.platformTypeId === props.dashboard?.platformTypeId)?.defaultDimensions
      ?? props.catalog?.[0]?.defaultDimensions
      ?? [],
    [props.catalog, props.dashboard?.platformTypeId],
  )
  // Single resolution point for every drill decision on this widget.
  const drillOrder = useMemo(
    () => dataSource.drilldownWidget
      ? resolveDrillOrder(props.widget, drilldownHierarchy, clientCatalogDimensions)
      : createDisabledDrillOrder([]),
    [dataSource.drilldownWidget, props.widget, drilldownHierarchy, clientCatalogDimensions],
  )
  const drillDisabledWarning = drillOrder.mode === 'disabled' && props.mode === 'builder' && !printMode
    ? `Drilldown is disabled for this widget${drillOrder.invalidFields.length > 0
      ? `: ${drillOrder.invalidFields.map((field) => formatFieldDisplayName(field)).join(', ')} ${drillOrder.invalidFields.length === 1 ? 'is' : 'are'} not available for this client.`
      : ': drillDownDimensions needs at least two valid dimensions.'}`
    : null
  const fieldSelectionOptions = useMemo(
    () => getFieldSelectionDimensions(props.widget.dimensions, drillOrder, isTableLikeViz(effectiveViz)).map((dimension) => ({
      value: dimension.value || dimension.name,
      label: formatFieldDisplayName(dimension.name),
    })),
    [drillOrder, effectiveViz, props.widget.dimensions],
  )
  const legacySelectedGroupByDimension = legacyMode ? props.widgetState.groupByDimension : null
  const engineSelectedGroupByDimension = legacyMode ? null : props.session.query.groupByDimension
  const engineSelectedGroupByDimensions = legacyMode ? null : props.session.query.groupByDimensions
  const widgetRuntimeState = props.runtimeState.widgetStateById[props.widget.id]
  const legacyRuntimeGroupByDimensions = legacyMode ? widgetRuntimeState?.groupByDimensions : null
  const selectedDimension = useMemo(() => {
    const daxDimensions = props.widget.daxGenerationRequest?.dimensions ?? []
    const defaultDimensions = props.widget.type === 'pivotTable'
      ? daxDimensions.slice(0, 2)
      : [props.widget.rowDimension ?? daxDimensions[0] ?? null]

    if (legacyMode) {
      const rawSelection = legacyRuntimeGroupByDimensions?.length
        ? legacyRuntimeGroupByDimensions
        : [legacySelectedGroupByDimension ?? defaultDimensions[0] ?? null]
      return limitDimensionsForViz(effectiveViz, normalizeDimensionSelection(props.widget, rawSelection))
    }

    const rawSelection = engineSelectedGroupByDimensions?.length
      ? engineSelectedGroupByDimensions
      : [engineSelectedGroupByDimension ?? defaultDimensions[0] ?? null]
    return limitDimensionsForViz(effectiveViz, normalizeDimensionSelection(props.widget, rawSelection))
  }, [
    engineSelectedGroupByDimension,
    engineSelectedGroupByDimensions,
    effectiveViz,
    legacyMode,
    legacyRuntimeGroupByDimensions,
    legacySelectedGroupByDimension,
    props.widget,
  ])
  const showBlockingLoader = legacyMode
    ? sourceState.status === 'loading'
    : (props.session.status === 'idle' || props.session.status === 'loading') && !hasRenderableData(props.session)
  const showInlineError = !legacyMode && props.session.status === 'error' && hasRenderableData(props.session)
  const exportWidget = dataSource.exportWidget
  const canExportVisualData = Boolean(exportWidget) && isExportableViz(effectiveViz)

  async function handleExportVisualData() {
    if (!exportWidget || !props.dashboard || !props.clientId || !hasWidgetHydrationPayload(props.widget) || !canExportVisualData) return

    const exportRuntimeState = {
      ...props.runtimeState,
      widgetStateById: {
        ...props.runtimeState.widgetStateById,
        [props.widget.id]: {
          ...props.runtimeState.widgetStateById[props.widget.id],
          groupByDimension: selectedDimension[0] ?? null,
          groupByDimensions: selectedDimension,
          measureParameterSelections,
        },
      },
    }
    const effectiveDaxGenerationRequest = buildEffectiveDaxGenerationRequest(props.dashboard, props.widget, exportRuntimeState)

    setIsExporting(true)
    try {
      const result = await exportWidget({
        clientId: props.clientId,
        dashboardId: props.dashboardId,
        platformTypeId: props.dashboard.platformTypeId,
        widget: props.widget,
        widgetType: effectiveViz,
        effectiveDaxGenerationRequest,
        dashboardFilters: props.dashboardFilters,
        selectedFilters: props.selectedFilters,
        page: 1,
        pageSize: sourceState.totalRows && sourceState.totalRows > 0
          ? sourceState.totalRows
          : sourceState.pageSize ?? resolveDefaultWidgetPageSize(props.widget),
        sortColumn: resolveWidgetSortKey(props.widget, displaySortColumn ?? sourceState.sortColumn ?? defaultSortColumn),
        sortDirection: displaySortDirection,
        groupByDimension: selectedDimension[0] ?? null,
        groupByDimensions: selectedDimension,
        applyPreviewRowLimit: false,
        fallbackState: sourceState,
      })
      downloadBlob(result.blob, result.filename)
      addToast('Visuals data downloaded.', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to download visuals data.', 'error')
    } finally {
      setIsExporting(false)
    }
  }

  async function fetchWidgetPage(next: {
    page?: number
    sortColumn?: string | null
    sortDirection?: 'asc' | 'desc'
    groupByDimensions?: string[] | null
    measureParameterSelections?: Record<string, string>
  }) {
    if (!legacyMode || !props.dashboard || !props.clientId || !hasWidgetHydrationPayload(props.widget)) return
    const requestId = ++fetchRequestRef.current
    const previousState = props.widgetState
    const shouldPreserveLiveState = previousState.status === 'live'
    const loadingState: WidgetHydrationState = shouldPreserveLiveState
      ? {
          ...previousState,
          error: null,
        }
      : {
          ...previousState,
          status: 'loading',
          stage: 'execute_dax_query',
          error: null,
        }
    props.onWidgetStateChange(props.widget.id, loadingState)
    setIsFetching(true)
    try {
      const nextRuntimeState = {
        ...props.runtimeState,
        widgetStateById: {
          ...props.runtimeState.widgetStateById,
          [props.widget.id]: {
            ...props.runtimeState.widgetStateById[props.widget.id],
            groupByDimension: next.groupByDimensions?.[0]
              ?? previousState.groupByDimension
              ?? props.runtimeState.widgetStateById[props.widget.id]?.groupByDimension
              ?? null,
            groupByDimensions: next.groupByDimensions
              ?? props.runtimeState.widgetStateById[props.widget.id]?.groupByDimensions
              ?? (previousState.groupByDimension ? [previousState.groupByDimension] : null),
            measureParameterSelections: next.measureParameterSelections ?? measureParameterSelections,
          },
        },
      }

      const effectiveDaxGenerationRequest = props.dashboard
        ? buildEffectiveDaxGenerationRequest(props.dashboard, props.widget, nextRuntimeState)
        : props.widget.daxGenerationRequest
      const base = {
        clientId: props.clientId,
        platformTypeId: props.dashboard.platformTypeId,
        widget: props.widget,
        effectiveDaxGenerationRequest,
        dashboardFilters: props.dashboardFilters,
        selectedFilters: buildLegacySelectedFilters(props.dashboard, nextRuntimeState),
        page: next.page ?? 1,
        pageSize: previousState.pageSize ?? resolveDefaultWidgetPageSize(props.widget),
        sortColumn: resolveWidgetSortKey(props.widget, next.sortColumn ?? previousState.sortColumn ?? defaultSortColumn),
        sortDirection: next.sortDirection ?? previousState.sortDirection ?? defaultSortDirection,
        groupByDimension: next.groupByDimensions?.[0] ?? previousState.groupByDimension ?? null,
        groupByDimensions: next.groupByDimensions
          ?? props.runtimeState.widgetStateById[props.widget.id]?.groupByDimensions
          ?? (previousState.groupByDimension ? [previousState.groupByDimension] : null),
        fallbackState: previousState,
      }
      const result = await dataSource.hydrateWidget(base)
      if (requestId !== fetchRequestRef.current) return
      const nextState = toWidgetState(result)
      props.onWidgetRuntimeStateChange?.(props.widget.id, {
        groupByDimension: nextState.groupByDimension,
        groupByDimensions: next.groupByDimensions ?? (nextState.groupByDimension ? [nextState.groupByDimension] : null),
      })
      props.onWidgetStateChange(props.widget.id, nextState)
    } catch (error) {
      if (requestId !== fetchRequestRef.current) return
      props.onWidgetStateChange(props.widget.id, {
        ...previousState,
        status: 'error',
        error: error instanceof Error ? error.message : 'Failed to fetch widget data.',
      })
    } finally {
      if (requestId === fetchRequestRef.current) {
        setIsFetching(false)
      }
    }
  }

  function handlePageChange(page: number) {
    setExpandedRows({})
    setLoadingRowKeys({})
    if (legacyMode) {
      if (!usesBackendPagination) {
        setLocalPage(page)
        return
      }
      void fetchWidgetPage({ page })
      return
    }
    props.onPage(props.widget.id, page)
  }

  function handleSortChange(column: string | null, direction: 'asc' | 'desc') {
    setExpandedRows({})
    setLoadingRowKeys({})
    if (legacyMode) {
      setLocalPage(1)
      setPendingSortState({ column, direction })
      if (sortDebounceRef.current) clearTimeout(sortDebounceRef.current)
      sortDebounceRef.current = setTimeout(() => {
        setPendingSortState(null)
        void fetchWidgetPage({ page: 1, sortColumn: column, sortDirection: direction })
      }, 1000)
      return
    }
    props.onSort(props.widget.id, column, direction)
  }

  function handleDimensionChange(value: string | string[]) {
    setExpandedRows({})
    setLoadingRowKeys({})
    const nextValue = Array.isArray(value) ? value : value ? [value] : []
    const normalizedValue = limitDimensionsForViz(effectiveViz, nextValue)

    if (legacyMode) {
      setLocalPage(1)
      if (dimensionDebounceRef.current) clearTimeout(dimensionDebounceRef.current)
      dimensionDebounceRef.current = setTimeout(() => {
        void fetchWidgetPage({ page: 1, groupByDimensions: normalizedValue.length > 0 ? normalizedValue : null })
      }, 400)
      return
    }
    props.onDimensionChange(props.widget.id, normalizedValue.length > 0 ? normalizedValue : null)
  }

  function handleTrendGranularityChange(value: DashboardTrendGranularity) {
    setTrendGranularity(value)
    setLocalPage(1)
    if (!legacyMode) {
      props.onPage(props.widget.id, 1)
    }
  }

  function getExpansionRowKey(row: Record<string, unknown>, columns: string[], parentKey = '') {
    return buildExpansionRowKey(row, columns, parentKey)
  }

  function getAvailableDrilldownFields() {
    // The platform's own hierarchy fields (from the server) plus anything
    // this widget already knows about, so drilldown offers the full chain
    // even when a particular widget doesn't have every level in its
    // metadata. No hard-coded platform-specific field names.
    //
    // Pivot column fields are a display concern, not a drill level. Exclude
    // them so a column that happens to sit on the next hierarchy level can't
    // be offered as a global-mode drill target. Override levels are explicit
    // (drillDownDimensions) and always stay reachable. Fields are compared by
    // label, since columnFields may hold DAX refs while data.columns holds
    // bare names.
    const pivotColumnFields = (props.widget.columnFields ?? []).filter((field): field is string => Boolean(field))
    const isPivotColumnField = (field: string) => pivotColumnFields
      .some((pivotField) => labelsMatch(stripTableQualifier(pivotField), stripTableQualifier(field)))
    const overrideDimensions = drillOrder.mode === 'override' ? drillOrder.dimensions : []
    const candidates = [
      ...drilldownDimensions,
      ...(drillOrder.mode === 'override' ? [] : drillOrder.dimensions),
      ...data.columns,
      ...props.widget.dimensions.flatMap((dimension) => [dimension.value, dimension.name]),
      ...(props.widget.daxGenerationRequest?.dimensions ?? []),
    ].filter((field): field is string => Boolean(field) && !isPivotColumnField(field))
    return Array.from(new Set([...overrideDimensions.filter(Boolean), ...candidates]))
  }

  function resolveTableRowField(row: Record<string, unknown>, columns: string[], preferredField?: string | null) {
    if (preferredField) {
      const matchingColumn = findDrilldownColumn(columns, preferredField)
      if (matchingColumn && row[matchingColumn] !== null && row[matchingColumn] !== undefined) return matchingColumn
      if (row[preferredField] !== null && row[preferredField] !== undefined) return preferredField
    }

    return [...columns, ...Object.keys(row)].find((field) => {
      const matchingField = findDrilldownColumn(Object.keys(row), field) ?? field
      return drillOrder.contains(field)
        && row[matchingField] !== null
        && row[matchingField] !== undefined
        && String(row[matchingField]).trim().length > 0
    }) ?? null
  }

  function canExpandTableRow(row: Record<string, unknown>, columns: string[], parentKey?: string) {
    if (drillOrder.mode === 'disabled') return false
    const parentResult = parentKey ? expandedRows[parentKey] : null
    const currentField = resolveTableRowField(row, columns, parentResult?.dimension ?? drillOrder.root(columns))
    if (!currentField || drillOrder.next(currentField, getAvailableDrilldownFields()) === null) return false
    const currentColumn = findDrilldownColumn(columns, currentField) ?? currentField
    const currentValue = row[currentColumn]
    return currentValue !== null
      && currentValue !== undefined
      && String(currentValue).trim().length > 0
      && drillOrder.contains(currentField)
  }

  async function loadTableRowExpand(
    row: Record<string, unknown>,
    columns: string[],
    parentKey: string | undefined,
    drilldownDimension: string,
  ) {
    const drilldownWidget = dataSource.drilldownWidget
    if (!drilldownWidget || !isTableLikeViz(effectiveViz) || drillOrder.mode === 'disabled') return
    const rowKey = getExpansionRowKey(row, columns, parentKey ?? '')
    if (expandedRows[rowKey]) {
      setExpandedRows((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== rowKey && !key.startsWith(`${rowKey}/`))))
      return
    }

    const parentResult = parentKey ? expandedRows[parentKey] : null
    const parentPath: DrilldownPathFilter[] = parentResult?.path ? [...parentResult.path] : []
    const currentField = resolveTableRowField(row, columns, parentResult?.dimension ?? drillOrder.root(columns))
    const currentColumn = currentField ? findDrilldownColumn(columns, currentField) ?? currentField : null
    const currentValue = currentColumn ? row[currentColumn] : null
    if (!currentField || currentValue === null || currentValue === undefined || !String(currentValue).trim()) return
    const path = [...parentPath, { field: currentField, value: String(currentValue) }]
    const primaryFilter = path[0]
    if (!primaryFilter) return
    if (!drilldownDimension) return

    // A pivot's child rows carry its column field too, so they fill the same month (or other) columns.
    const pivotColumnField = effectiveViz === 'pivotTable' ? resolvePivotColumnField(props.widget, data) : null

    setLoadingRowKeys((current) => ({ ...current, [rowKey]: true }))
    try {
      const effectiveDaxGenerationRequest = props.dashboard
        ? buildEffectiveDaxGenerationRequest(props.dashboard, props.widget, props.runtimeState)
        : props.widget.daxGenerationRequest
      const result = await drilldownWidget({
        clientId: props.clientId,
        platformTypeId: props.dashboard?.platformTypeId ?? 0,
        dashboardId: props.dashboardId,
        widget: props.widget,
        effectiveDaxGenerationRequest,
        dashboardFilters: props.dashboardFilters,
        selectedFilters: legacyMode ? buildLegacySelectedFilters(props.dashboard, props.runtimeState) : props.selectedFilters,
        page: 1,
        pageSize: 200,
        sortColumn: null,
        sortDirection: 'desc',
        groupByDimension: null,
        groupByDimensions: null,
        fallbackState: sourceState,
        field: primaryFilter.field,
        value: primaryFilter.value,
        dimension: drilldownDimension,
        parentFilters: path,
        drillDownDimensions: drillOrder.mode === 'override' ? drillOrder.dimensions : undefined,
        pivotColumnDimensions: pivotColumnField ? [pivotColumnField] : undefined,
      })
      setExpandedRows((current) => ({
        ...current,
        [rowKey]: { rows: result.rows, columns: result.columns, dimension: drilldownDimension, path },
      }))
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to load drilldown rows.', 'error')
    } finally {
      setLoadingRowKeys((current) => ({ ...current, [rowKey]: false }))
    }
  }

  function handleTableRowExpand(row: Record<string, unknown>, columns: string[], parentKey?: string) {
    const rowKey = getExpansionRowKey(row, columns, parentKey ?? '')
    if (expandedRows[rowKey]) {
      setExpandedRows((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== rowKey && !key.startsWith(`${rowKey}/`))))
      return
    }

    const parentResult = parentKey ? expandedRows[parentKey] : null
    const currentField = resolveTableRowField(row, columns, parentResult?.dimension ?? drillOrder.root(columns))
    if (!currentField) {
      addToast('This row does not contain a supported drilldown field.', 'error')
      return
    }
    const nextDimension = drillOrder.mode === 'disabled' ? null : drillOrder.next(currentField, getAvailableDrilldownFields())
    const options = nextDimension ? [nextDimension] : []
    if (options.length === 0) {
      addToast('No next drilldown dimension is available for this row.', 'error')
      return
    }

    // When the widget declares its own drill order (override mode), the next
    // level is fully determined by drillDownDimensions — there is nothing to
    // choose, so skip the picker modal and drill straight in.
    if (drillOrder.mode === 'override' && nextDimension) {
      void loadTableRowExpand(row, columns, parentKey, nextDimension)
      return
    }

    setPendingExpansion({
      row,
      columns,
      parentKey,
      rowKey,
      currentField,
      dimension: options[0] ?? '',
      options,
    })
  }

  if (props.widget.type === 'insight') {
    return (
      <Card key={props.widget.id} title={widgetTitle} style={viewerCardStyles?.root} styles={viewerCardStyles ? { header: viewerCardStyles.header, title: viewerCardStyles.title, body: viewerCardStyles.body } : undefined}>
        <Alert type="info" showIcon message="Narrative widgets are generated by the Agent during dashboard and data-analysis conversations." />
      </Card>
    )
  }

  const refreshIndicator = printMode
    ? null
    : legacyMode
      ? (isFetching ? <Spin size="small" /> : null)
      : (props.session.status === 'refreshing' ? <Spin size="small" /> : null)
  // A gauge shows the period it covers where other widgets show their description, and carries
  // its gap-to-target badge in the header.
  const isGauge = effectiveViz === 'gauge'
  const gaugeBadge = isGauge ? resolveGaugeBadge(resolveGaugeValues(data, props.widget)) : null
  const gaugeSubtitle = isGauge
    ? describeDateRangeSubtitle(
        props.runtimeState.dateRange,
        props.dashboard ? getCatalogDatePresetOptions(props.dashboard, props.catalog) : [],
      )
    : null

  // Dropdowns that render in the controls row. The conditions match the JSX below.
  const showFieldSelection = allowFieldSelection && fieldSelectionOptions.length > 1 && effectiveViz !== 'gauge'
  const showMeasureParameters = allowFieldSelection && effectiveViz !== 'gauge' && (props.widget.measureParameters ?? []).length > 0
  const hasCardDropdown = showSortControls || showFieldSelection || showMeasureParameters || showTrendGranularityControl
  // Description and controls are hidden while the body is loading or in an error state, as before.
  const isBodyReady = !showBlockingLoader && !(sourceState.status === 'error' && !showInlineError)
  const descriptionClass = isViewerChrome
    ? '!mb-0 min-w-0 flex-1 text-[13px] leading-[1.5] text-[#667085]'
    : '!mb-0 min-w-0 flex-1 text-sm text-gray-500'
  const descriptionNode = isGauge
    ? (gaugeSubtitle ? <Typography.Paragraph className={descriptionClass}>{gaugeSubtitle}</Typography.Paragraph> : null)
    : (props.widget.description?.trim()
        ? <Typography.Paragraph className={descriptionClass}>{props.widget.description}</Typography.Paragraph>
        : null)
  const exportButton = !printMode && canExportVisualData ? (
    <Tooltip title="Download Visuals Data">
      <Button
        size="small"
        type="default"
        icon={<DownloadOutlined />}
        loading={isExporting}
        disabled={showBlockingLoader || sourceState.status === 'error'}
        onClick={() => { void handleExportVisualData() }}
        aria-label="Download Visuals Data"
        className="!flex !h-[30px] !w-[30px] !min-w-0 !items-center !justify-center !rounded-md !border-[#d0d6de] !bg-white !p-0 !text-[#475467] hover:!border-[#0f8f82] hover:!text-[#0f8f82] [&_.anticon]:text-[15px]"
      />
    </Tooltip>
  ) : null
  const vizSwitcher = allowVizSwitch && availableTypes.length > 1 ? (
    <div className="flex items-center gap-0.5">
      {getVizSwitcherOptions(props.widget, availableTypes).map(({ key, icon, label }) => (
        <Tooltip key={key} title={label}>
          <Button
            size="small"
            type="text"
            icon={icon}
            onClick={() => {
              setVizOverride(key)
            }}
            className={effectiveViz === key ? 'feature-soft-text feature-soft-surface' : 'text-gray-400 hover:text-gray-600'}
            aria-label={`Switch to ${label} view`}
          />
        </Tooltip>
      ))}
    </div>
  ) : null
  const toolsGroup = exportButton || vizSwitcher ? (
    <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
      {exportButton}
      {vizSwitcher}
    </div>
  ) : null
  // Row 1: the description alone when a dropdown row follows; otherwise the description and tools share the row.
  const showToolsOnRowOne = !hasCardDropdown && isBodyReady && toolsGroup !== null
  const descriptionRow = descriptionNode !== null || showToolsOnRowOne
    ? (
        <div className="flex min-h-[30px] items-center gap-3">
          {descriptionNode}
          {showToolsOnRowOne ? toolsGroup : null}
        </div>
      )
    : null

  return (
    <Card
      key={props.widget.id}
      title={widgetTitle}
      className={printWidgetClass}
      style={viewerCardStyles?.root}
      styles={viewerCardStyles ? { header: viewerCardStyles.header, title: viewerCardStyles.title, body: viewerCardStyles.body } : undefined}
      extra={gaugeBadge || refreshIndicator ? (
        <Space size={8}>
          {gaugeBadge ? <GaugeBadge badge={gaugeBadge} /> : null}
          {refreshIndicator}
        </Space>
      ) : null}
    >
      <Space direction="vertical" size={16} className="w-full">
        {descriptionRow}

        {showBlockingLoader ? (
          <div className="flex min-h-[240px] items-center justify-center">
            <Spin tip="Refreshing data…" />
          </div>
        ) : sourceState.status === 'error' && !(showInlineError) ? (
          <Alert type="error" showIcon message="Failed to load live widget data" description={sourceState.error ?? 'Unknown widget hydration error'} />
        ) : (
          <>
            {drillDisabledWarning ? (
              <Alert type="warning" showIcon message={drillDisabledWarning} />
            ) : null}

            {showInlineError ? (
              <Alert
                type="warning"
                showIcon
                message="Showing previous widget data"
                description={props.session.error ?? 'The latest refresh failed.'}
              />
            ) : null}

            {hasCardDropdown && !printMode ? (
              <div className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 flex-col items-stretch justify-start gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  {showSortControls ? (
                    <SortControls
                      columns={data.columns}
                      rows={data.rows}
                      vizType={getWidgetVizType(effectiveViz)}
                      sortColumn={displaySortColumn}
                      sortDirection={displaySortDirection}
                      onChange={handleSortChange}
                      sortOptions={sortOptions}
                    />
                  ) : null}
                  {showFieldSelection ? (
                    <Select
                      size="small"
                      value={usesSingleDimensionSelection ? selectedDimension[0] : selectedDimension}
                      {...(usesSingleDimensionSelection ? {} : { mode: 'multiple' as const })}
                      onChange={handleDimensionChange}
                      options={fieldSelectionOptions}
                      className={`${CARD_SELECT_CLASS} w-full sm:min-w-[220px] sm:w-auto`}
                      placeholder="Field selection"
                    />
                  ) : null}
                  {showMeasureParameters ? (props.widget.measureParameters ?? []).map((parameter) => (
                    <Select
                      key={parameter.id}
                      size="small"
                      value={measureParameterSelections[parameter.id]}
                      onChange={(value) => handleMeasureParameterChange(parameter.id, value)}
                      options={parameter.options.map((option) => ({ value: option.id, label: option.label }))}
                      className={`${CARD_SELECT_CLASS} w-full sm:min-w-[180px] sm:w-auto`}
                      placeholder={parameter.label}
                      aria-label={parameter.label}
                    />
                  )) : null}
                  {showTrendGranularityControl ? (
                    <Select
                      size="small"
                      value={trendGranularity}
                      onChange={handleTrendGranularityChange}
                      options={trendGranularityOptions(props.widget.trendGranularity)}
                      className={`${CARD_SELECT_CLASS} w-full sm:min-w-[150px] sm:w-auto`}
                      aria-label="Trend granularity"
                    />
                  ) : null}
                </div>
                {toolsGroup}
              </div>
            ) : null}

            {effectiveViz === 'kpi' ? (
              <KpiViz data={data} />
            ) : effectiveViz === 'bar' ? (
              <BarViz data={data} page={displayPage} onPageChange={handlePageChange} showPagination={!printMode} printMode={printMode} orientation={props.widget.orientation} />
            ) : effectiveViz === 'groupedBar' ? (
              <GroupedBarViz data={data} page={displayPage} onPageChange={handlePageChange} showPagination={!printMode} printMode={printMode} orientation={props.widget.orientation} />
            ) : effectiveViz === 'line' ? (
              <LineViz data={data} printMode={printMode} trendGranularity={trendGranularity} />
            ) : effectiveViz === 'donut' ? (
              <DonutViz data={data} printMode={printMode} />
            ) : effectiveViz === 'gauge' ? (
              <GaugeViz data={data} widget={props.widget} printMode={printMode} />
            ) : effectiveViz === 'dataBarTable' ? (
              <DataBarTableViz
                data={data}
                page={displayPage}
                onPageChange={handlePageChange}
                showPagination={!printMode}
                onSortChange={printMode ? undefined : handleSortChange}
                sortColumn={displayTableSortColumn}
                sortDirection={displaySortDirection}
                expandedRows={expandedRows}
                loadingRowKeys={loadingRowKeys}
                onExpandRow={handleTableRowExpand}
                buildRowKey={getExpansionRowKey}
                canExpandRow={canExpandTableRow}
                pivot={!!props.widget.columnFields?.length}
              />
            ) : effectiveViz === 'pivotTable' ? (
              <PivotTableViz
                data={data}
                page={displayPage}
                onPageChange={handlePageChange}
                showPagination={!printMode}
                expandedRows={expandedRows}
                loadingRowKeys={loadingRowKeys}
                onExpandRow={drillOrder.mode === 'disabled' ? undefined : handleTableRowExpand}
                canExpandRow={canExpandTableRow}
                buildRowKey={getExpansionRowKey}
              />
            ) : (
              <TableViz
                data={data}
                page={displayPage}
                onPageChange={handlePageChange}
                showPagination={!printMode}
                onSortChange={printMode ? undefined : handleSortChange}
                sortColumn={displayTableSortColumn}
                sortDirection={displaySortDirection}
                expandedRows={expandedRows}
                loadingRowKeys={loadingRowKeys}
                onExpandRow={handleTableRowExpand}
                buildRowKey={getExpansionRowKey}
                canExpandRow={canExpandTableRow}
              />
            )}
          </>
        )}
      </Space>

      <Modal
        title="Choose drilldown dimension"
        open={!printMode && pendingExpansion !== null}
        okText="Load group"
        cancelText="Cancel"
        confirmLoading={pendingExpansion ? loadingRowKeys[pendingExpansion.rowKey] === true : false}
        okButtonProps={{ disabled: !pendingExpansion?.dimension }}
        onCancel={() => setPendingExpansion(null)}
        onOk={() => {
          if (!pendingExpansion) return
          const selection = pendingExpansion
          setPendingExpansion(null)
          void loadTableRowExpand(selection.row, selection.columns, selection.parentKey, selection.dimension)
        }}
      >
        {pendingExpansion ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              Select any level beneath <span className="font-medium text-gray-700">{formatFieldDisplayName(pendingExpansion.currentField)}</span> to drill down to.
            </p>
            <Select
              className="w-full"
              value={pendingExpansion.dimension}
              options={pendingExpansion.options.map((dimension) => ({
                value: dimension,
                label: formatFieldDisplayName(dimension),
              }))}
              onChange={(dimension) => setPendingExpansion((current) => current ? { ...current, dimension } : current)}
            />
          </div>
        ) : null}
      </Modal>

      {footer ? <div className="-mx-6 -mb-6 mt-4 overflow-hidden rounded-b-lg">{footer}</div> : null}
    </Card>
  )
}
